import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Camera, FileText, Image as ImageIcon, BookOpen, Users, LogOut, Sparkles, User, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Footer from "../components/Footer";

const COMMUNITY_URL = "https://www.facebook.com/groups/brandsnap.pro/";

const sideNav = [
  { to: "/app", label: "Dashboard", icon: Home, end: true, testid: "nav-dashboard" },
  { to: "/app/system", label: "The Photo System", icon: Camera, testid: "nav-system" },
  { to: "/app/step1", label: "Reference Photos", icon: ImageIcon, testid: "nav-step1" },
  { to: "/app/prompts", label: "Prompt Library", icon: FileText, testid: "nav-prompts" },
  { to: "/app/gallery", label: "My Gallery", icon: ImageIcon, testid: "nav-gallery" },
  { to: "/app/resources", label: "Resources", icon: BookOpen, testid: "nav-resources" },
  { to: "/app/step2", label: "Brand Profile", icon: User, testid: "nav-brand-profile" },
];

const mobileNav = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/system", label: "System", icon: Camera },
  { to: "/app/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/app/resources", label: "Library", icon: BookOpen },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/");
  };

  const initials = (user?.full_name || "B").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#1A1F3D] text-white sticky top-0 h-screen">
        <div className="px-6 py-6 flex items-center gap-2 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-[#D4A853] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#1A1F3D]" />
          </div>
          <span className="font-display text-xl">BrandSnapPro™</span>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 scrollbar-clean overflow-y-auto">
          {sideNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#D4A853] text-[#1A1F3D]" : "text-white/80 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="w-5 h-5" strokeWidth={1.6} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          <a
            href={COMMUNITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-community"
          >
            <Users className="w-5 h-5" strokeWidth={1.6} />
            <span>Community</span>
            <span className="ml-auto text-xs text-white/40">↗</span>
          </a>
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 border-t border-white/10 pt-4 ${
                  isActive ? "bg-[#D4A853] text-[#1A1F3D]" : "text-[#D4A853] hover:bg-white/5"
                }`
              }
            >
              <ShieldCheck className="w-5 h-5" strokeWidth={1.6} />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#D4A853] text-[#1A1F3D] font-semibold flex items-center justify-center text-sm">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="text-xs text-white/50 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={onLogout} className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#1A1F3D] text-white h-14 flex items-center justify-between px-4 border-b border-white/10">
        <Link to="/app" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#D4A853] flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-[#1A1F3D]" />
          </div>
          <span className="font-display text-lg">BrandSnapPro™</span>
        </Link>
        <button onClick={onLogout} className="text-white/80 text-sm flex items-center gap-1" data-testid="logout-btn-mobile">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden h-14" />
        <div className="px-5 md:px-10 py-8 md:py-12 pb-28 md:pb-12 flex-1">
          <Outlet />
        </div>
        <Footer />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#e5e7eb] flex items-center justify-around px-2 py-2 z-30">
        {mobileNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-md text-xs ${isActive ? "text-[#1A1F3D] font-semibold" : "text-[#52525b]"}`
            }
          >
            <n.icon className={`w-5 h-5`} strokeWidth={1.6} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
